import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import type { RedeemPreview, RedeemResult } from '@mlm/shared';
import { apiErrorMessage, kits } from '../lib/api';
import { qk } from '../lib/queries';
import { Block, CoachButton, Loading, Screen, inputClass } from '../components/primitives';

/**
 * Code+PIN activation, in two deliberate steps.
 *
 * The preview is not decoration: redeeming decides who the new slot is
 * SPONSORED BY, which is permanent and worth real money to that upline. A
 * member gets to see the resolved sponsor and confirm it before anything is
 * written, which is the same reason the server exposes a dry run at all.
 */
export function VaultRedeemPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [code, setCode] = useState(params.get('code') ?? '');
  const [pin, setPin] = useState(params.get('pin') ?? '');
  const [sponsorCode, setSponsorCode] = useState('');
  const [preview, setPreview] = useState<RedeemPreview | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [done, setDone] = useState<RedeemResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const terms = useQuery({
    queryKey: qk.activationTerms,
    queryFn: kits.activationTerms,
    staleTime: 5 * 60_000,
  });
  const termsRequired = terms.data?.required === true;

  const check = useMutation({
    mutationFn: () =>
      kits.preview({
        code: code.trim(),
        pin: pin.trim(),
        ...(sponsorCode.trim() ? { sponsorCode: sponsorCode.trim() } : {}),
      }),
    onSuccess: setPreview,
    onError: (e) => {
      setPreview(null);
      setError(e);
    },
  });

  const redeem = useMutation({
    mutationFn: () =>
      kits.redeem({
        code: code.trim(),
        pin: pin.trim(),
        ...(sponsorCode.trim() ? { sponsorCode: sponsorCode.trim() } : {}),
        ...(termsRequired ? { termsAccepted: true, termsVersion: terms.data!.version } : {}),
      }),
    onSuccess: async (res) => {
      setDone(res);
      // A new slot changes the vault, the wallet and the tree all at once.
      await qc.invalidateQueries({ queryKey: qk.myCodes });
      await qc.invalidateQueries({ queryKey: qk.mySlots });
      await qc.invalidateQueries({ queryKey: qk.walletTotals });
    },
    onError: setError,
  });

  if (done) {
    return (
      <Screen eyebrow="Activated" title="Your slot is live" back="/vault">
        <Block eyebrow="Slot code" title={done.slotCode}>
          {done.successMessage ?? 'Your membership slot is active and placed in the tree.'}
        </Block>
        <div className="space-y-3 px-5 py-6">
          <CoachButton kind="gold" block onClick={() => navigate('/network')}>
            See your network
          </CoachButton>
          <CoachButton block onClick={() => navigate('/vault')}>
            Back to your codes
          </CoachButton>
        </div>
      </Screen>
    );
  }

  return (
    <Screen eyebrow="Activate" title="Use a code" back="/vault">
      <Block eyebrow="Your code" title="Code and PIN">
        <div className="mt-3 space-y-3">
          <div>
            <label className="eyebrow" htmlFor="rk-code">
              Code
            </label>
            <input
              id="rk-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setPreview(null);
              }}
              autoCapitalize="characters"
              spellCheck={false}
              className={`${inputClass} mt-1 font-mono tracking-wider`}
            />
          </div>
          <div>
            <label className="eyebrow" htmlFor="rk-pin">
              PIN
            </label>
            <input
              id="rk-pin"
              value={pin}
              inputMode="numeric"
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ''));
                setPreview(null);
              }}
              className={`${inputClass} mt-1 font-mono tracking-wider`}
            />
          </div>
          <div>
            <label className="eyebrow" htmlFor="rk-sponsor">
              Sponsor code (optional)
            </label>
            <input
              id="rk-sponsor"
              value={sponsorCode}
              onChange={(e) => {
                setSponsorCode(e.target.value);
                setPreview(null);
              }}
              placeholder="Leave blank to use your default"
              className={`${inputClass} mt-1`}
            />
          </div>
        </div>
      </Block>

      {preview && <PreviewBlock preview={preview} />}

      {termsRequired && preview && terms.data && (
        <Block eyebrow="Before you continue" title="Activation terms">
          <div className="max-h-48 overflow-y-auto border border-border px-4 py-3 text-[0.88rem] leading-relaxed text-ink/80">
            {terms.data.text}
          </div>
          <label className="mt-3 flex items-start gap-3 text-[0.93rem] text-ink">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1"
            />
            I have read and accept these terms.
          </label>
        </Block>
      )}

      <div className="px-5 py-6">
        {error != null && (
          <p className="mb-4 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
            {apiErrorMessage(error)}
          </p>
        )}

        {!preview ? (
          <CoachButton
            kind="gold"
            block
            disabled={!code.trim() || !pin.trim() || check.isPending}
            onClick={() => {
              setError(null);
              check.mutate();
            }}
          >
            <KeyRound className="h-4 w-4" aria-hidden />
            {check.isPending ? 'Checking' : 'Check this code'}
          </CoachButton>
        ) : (
          <>
            <CoachButton
              kind="gold"
              block
              disabled={redeem.isPending || (termsRequired && !accepted)}
              onClick={() => {
                setError(null);
                redeem.mutate();
              }}
            >
              {redeem.isPending ? 'Activating' : 'Activate my slot'}
            </CoachButton>
            <p className="mt-4 text-center text-[0.85rem] leading-relaxed text-ink/60">
              Activating is permanent. The sponsor shown above cannot be changed afterwards.
            </p>
          </>
        )}

        {terms.isPending && <Loading label="Loading terms" />}
      </div>
    </Screen>
  );
}

function PreviewBlock({ preview }: { preview: RedeemPreview }) {
  const cd = preview.slotType === 'cd' || preview.slotType === 'half_cd';
  return (
    <Block eyebrow="Check this is right" title="What this code does">
      <dl className="mt-1 space-y-3">
        <Row label="Membership" value={preview.membershipCode} />
        <Row
          label="Sponsor"
          value={`${preview.sponsor.ownerFullName ?? preview.sponsor.ownerEmail} (${preview.sponsor.slotCode})`}
        />
        {preview.slotType !== 'paid' && <Row label="Slot type" value={preview.slotType} />}
      </dl>
      {cd && preview.debtAmount && (
        <p className="mt-4 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] leading-relaxed text-ink/85">
          This slot starts with {preview.debtAmount} to settle. Commissions pay that down first
          before anything reaches your wallet.
        </p>
      )}
    </Block>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="eyebrow shrink-0">{label}</dt>
      <dd className="text-right text-[0.93rem] text-ink">{value}</dd>
    </div>
  );
}
